import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Bot,
	GitBranch,
	Image as ImageIcon,
	Layers,
	Repeat2,
	Sparkles,
} from "lucide-react";
import { FeatureGrid } from "@/components/feature-grid";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { MarketingSection } from "@/components/marketing-section";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/features/cross-posting";

export const Route = createFileRoute(path)({
	component: CrossPostingFeature,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "AgentReacher | Cross-Posting",
			description:
				"Cross-post once, publish everywhere. AgentReacher rewrites per platform and schedules native uploads to every connected channel.",
		}),
});

function CrossPostingFeature() {
	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="Feature: cross-posting"
				title="Cross-posting that doesn't sound recycled."
				description="One idea, every network. The agent rewrites per platform  -  tone, length, and format  -  so each post lands native."
			/>

			<MarketingSection
				title="What makes AgentReacher's cross-posting different"
				description="Most schedulers paste the same text everywhere. We rewrite for each network."
			>
				<FeatureGrid
					features={[
						{
							icon: Repeat2,
							title: "One source of truth",
							description:
								"Drop the idea once. The agent creates per-platform versions tuned for each audience.",
						},
						{
							icon: Bot,
							title: "Voice-matched, platform-aware",
							description:
								"Your tone stays consistent. Tone shape adapts to each network's norms.",
						},
						{
							icon: GitBranch,
							title: "Every platform supported",
							description:
								"Bluesky, X, Threads, LinkedIn, Instagram, TikTok, YouTube, Facebook, Pinterest.",
						},
						{
							icon: ImageIcon,
							title: "Format conversions",
							description:
								"Vertical video to Reels, Shorts, and TikTok. Long-form to threads. Photos to pins.",
						},
						{
							icon: Layers,
							title: "Per-platform timing",
							description:
								"Each version schedules into its own peak window, not a one-size-fits-all slot.",
						},
						{
							icon: Sparkles,
							title: "One-click cross-post",
							description:
								"From the post composer, pick which platforms to fan out to. The agent handles the rest.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection>
				<div className="mx-auto max-w-2xl space-y-4 text-center text-sm leading-relaxed text-muted-foreground">
					<p>
						Related:{" "}
						<Link
							to="/features/ai-scheduling"
							className="text-primary transition-colors hover:text-primary/80"
						>
							AI scheduling
						</Link>
						,{" "}
						<Link
							to="/features/calendar"
							className="text-primary transition-colors hover:text-primary/80"
						>
							unified calendar
						</Link>
						,{" "}
						<Link
							to="/blog"
							className="text-primary transition-colors hover:text-primary/80"
						>
							latest cross-posting guides
						</Link>
						.
					</p>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	);
}
